import React, { useState, useEffect } from "react";
import { Form, Input, Button, Radio, Select, Checkbox, Steps } from "antd";
import { Key, Sms, User, InfoCircle, TaskSquare } from "iconsax-react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/auth";
import { BRAZIL_STATES } from "../../constants/brazilStates";
import { COUNTRIES } from "../../constants/countries";
import {
  Container,
  FormContainer,
  Title,
  Subtitle,
  StepContainer,
  StepActions,
  InfoBox,
  RadioGroup,
} from "./styles";

const Register: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { signUp, loadingAuth, signInWithGoogle } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleRegister = async (values: any) => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { confirmPassword, ...rest } = values;
      await signUp({
        ...rest,
        type: "user",
        age: Number(rest.age),
      });
      navigate("/modules");
    } catch (error) {
      console.error("Registration failed:", error);
    }
  };

  const handleNext = async () => {
    try {
      await form.validateFields();
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        form.submit();
      }
    } catch (error) {
      console.error("Validation failed:", error);
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const getSubtitle = () => {
    if (currentStep === 0) return "Informações básicas para começar";
    if (currentStep === 1) return "Conte-nos mais sobre você";
    if (currentStep === 2) return "Regras da comunidade";
    return "Termos de uso";
  };

  return (
    <Container>
      <FormContainer>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Title>Crie sua conta</Title>
          <Subtitle>{getSubtitle()}</Subtitle>
        </div>

        <Steps
          current={currentStep}
          style={{ marginBottom: 40 }}
          items={[
            { title: "Dados" },
            { title: "Perfil" },
            { title: "Regras" },
            { title: "Termos" },
          ]}
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleRegister}
          requiredMark={false}
          initialValues={{
            isDeaf: false,
            gender: "prefiro-nao-informar",
            librasFluencyLevel: "Basico",
          }}
        >
          {currentStep === 0 && (
            <StepContainer>
              <Form.Item
                name="name"
                label="Qual seu nome?"
                rules={[
                  { required: true, message: "Por favor, insira seu nome!" },
                ]}
              >
                <Input
                  prefix={
                    <User
                      size={20}
                      color="var(--color-primary)"
                      variant="Bulk"
                    />
                  }
                  placeholder="Nome completo"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="email"
                label="Qual seu melhor e-mail?"
                rules={[
                  { required: true, message: "Por favor, insira seu e-mail!" },
                  { type: "email", message: "Insira um e-mail válido!" },
                ]}
              >
                <Input
                  prefix={
                    <Sms
                      size={20}
                      color="var(--color-primary)"
                      variant="Bulk"
                    />
                  }
                  placeholder="E-mail"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="password"
                label="Crie uma senha"
                rules={[
                  { required: true, message: "Por favor, insira sua senha!" },
                  {
                    min: 6,
                    message: "A senha deve ter pelo menos 6 caracteres!",
                  },
                ]}
              >
                <Input.Password
                  prefix={
                    <Key
                      size={20}
                      color="var(--color-primary)"
                      variant="Bulk"
                    />
                  }
                  placeholder="Senha"
                  size="large"
                />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Confirme sua senha"
                dependencies={["password"]}
                rules={[
                  { required: true, message: "Confirme sua senha!" },
                  ({ getFieldValue }) => ({
                    validator(_, value) {
                      if (!value || getFieldValue("password") === value) {
                        return Promise.resolve();
                      }
                      return Promise.reject(
                        new Error("As senhas não conferem!")
                      );
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={
                    <Key
                      size={20}
                      color="var(--color-primary)"
                      variant="Bulk"
                    />
                  }
                  placeholder="Repetir senha"
                  size="large"
                />
              </Form.Item>

              <Button
                type="default"
                onClick={signInWithGoogle}
                style={{
                  width: "100%",
                  height: 48,
                  marginBottom: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 10,
                  borderRadius: 8,
                  fontWeight: 600,
                }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 12-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Cadastrar-se com o Google
              </Button>
            </StepContainer>
          )}

          {currentStep === 1 && (
            <StepContainer>
              <Form.Item
                name="isDeaf"
                label="Você é surdo ou ouvinte?"
                rules={[{ required: true, message: "Selecione uma opção" }]}
              >
                <RadioGroup>
                  <Radio.Button value>Surdo</Radio.Button>
                  <Radio.Button value={false}>Ouvinte</Radio.Button>
                </RadioGroup>
              </Form.Item>

              <Form.Item
                name="age"
                label="Qual sua idade?"
                rules={[{ required: true, message: "Informe sua idade" }]}
              >
                <Input type="number" placeholder="Ex: 25" size="large" />
              </Form.Item>

              <Form.Item
                name="gender"
                label="Gênero"
                rules={[{ required: true, message: "Selecione seu gênero" }]}
              >
                <Select placeholder="Selecione" size="large">
                  <Select.Option value="masculino">Masculino</Select.Option>
                  <Select.Option value="feminino">Feminino</Select.Option>
                  <Select.Option value="nao-binario">Não-binário</Select.Option>
                  <Select.Option value="transgenero">Transgênero</Select.Option>
                  <Select.Option value="homem-trans">Homem Trans</Select.Option>
                  <Select.Option value="mulher-trans">
                    Mulher Trans
                  </Select.Option>
                  <Select.Option value="genero-fluido">
                    Gênero Fluido
                  </Select.Option>
                  <Select.Option value="agenero">Agênero</Select.Option>
                  <Select.Option value="outro">Outro</Select.Option>
                  <Select.Option value="prefiro-nao-informar">
                    Prefiro não informar
                  </Select.Option>
                </Select>
              </Form.Item>

              <Form.Item label="Sobre você (selecione o que se aplica)">
                <Form.Item
                  name="isParentOfDeafPerson"
                  valuePropName="checked"
                  noStyle
                >
                  <Checkbox>Sou pai/mãe de pessoa surda</Checkbox>
                </Form.Item>
                <div style={{ marginTop: 8 }}>
                  <Form.Item
                    name="isLibrasInterpreter"
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>Sou intérprete de Libras</Checkbox>
                  </Form.Item>
                </div>
                <div style={{ marginTop: 8 }}>
                  <Form.Item
                    name="isLibrasEnthusiast"
                    valuePropName="checked"
                    noStyle
                  >
                    <Checkbox>Sou entusiasta/estudante de Libras</Checkbox>
                  </Form.Item>
                </div>
              </Form.Item>

              <Form.Item
                name="country"
                label="País"
                rules={[{ required: true, message: "Selecione seu país" }]}
              >
                <Select
                  showSearch
                  placeholder="Selecione seu país"
                  size="large"
                  filterOption={(inp, opt) =>
                    (opt?.children as unknown as string)
                      .toLowerCase()
                      .includes(inp.toLowerCase())
                  }
                >
                  {COUNTRIES.map((c) => (
                    <Select.Option key={c.code} value={c.name}>
                      {c.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.country !== currentValues.country
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue("country") === "Brasil" && (
                    <>
                      <Form.Item
                        name="state"
                        label="Estado"
                        rules={[
                          { required: true, message: "Selecione seu estado" },
                        ]}
                      >
                        <Select placeholder="Selecione o estado" size="large">
                          {BRAZIL_STATES.map((s) => (
                            <Select.Option key={s.code} value={s.name}>
                              {s.name}
                            </Select.Option>
                          ))}
                        </Select>
                      </Form.Item>
                      <Form.Item
                        name="city"
                        label="Cidade"
                        rules={[
                          { required: true, message: "Informe sua cidade" },
                        ]}
                      >
                        <Input placeholder="Sua cidade" size="large" />
                      </Form.Item>
                    </>
                  )
                }
              </Form.Item>

              <Form.Item
                name="librasFluencyLevel"
                label="Qual seu nível de fluência em Libras?"
              >
                <Select placeholder="Selecione" size="large">
                  <Select.Option value="Basico">Básico</Select.Option>
                  <Select.Option value="Intermediario">
                    Intermediário
                  </Select.Option>
                  <Select.Option value="Avancado">Avançado</Select.Option>
                  <Select.Option value="Fluente">
                    Fluente / Nativo
                  </Select.Option>
                </Select>
              </Form.Item>
            </StepContainer>
          )}

          {currentStep === 2 && (
            <StepContainer>
              <InfoBox>
                <h3>
                  <InfoCircle size={24} color="var(--color-primary)" />
                  Regras da Comunidade
                </h3>
                <ul>
                  <li>Respeite todos os usuários da plataforma.</li>
                  <li>Não compartilhe conteúdo ofensivo ou inadequado.</li>
                  <li>O Bilingua é um ambiente de aprendizado e troca.</li>
                  <li>
                    Denuncie qualquer comportamento que viole estas regras.
                  </li>
                </ul>
              </InfoBox>
              <Form.Item
                name="acceptRules"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Você precisa aceitar as regras!")
                          ),
                  },
                ]}
              >
                <Checkbox>
                  Eu li e concordo com as regras da comunidade
                </Checkbox>
              </Form.Item>
            </StepContainer>
          )}

          {currentStep === 3 && (
            <StepContainer>
              <InfoBox>
                <h3>
                  <TaskSquare size={24} color="var(--color-primary)" />
                  Termos de Uso
                </h3>
                <div
                  style={{
                    maxHeight: 200,
                    overflowY: "auto",
                    paddingRight: 10,
                  }}
                >
                  <p>
                    Ao utilizar o Bilingua, você concorda com a coleta de dados
                    básicos para fins de personalização da experiência e
                    estatísticas de uso.
                  </p>
                  <p>
                    Seus dados não serão compartilhados com terceiros sem
                    consentimento prévio.
                  </p>
                  <p>Você é responsável pela segurança de sua conta e senha.</p>
                  <p>
                    Reservamos o direito de suspender contas que violem nossos
                    termos.
                  </p>
                </div>
              </InfoBox>
              <Form.Item
                name="acceptTerms"
                valuePropName="checked"
                rules={[
                  {
                    validator: (_, value) =>
                      value
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error("Você precisa aceitar os termos!")
                          ),
                  },
                ]}
              >
                <Checkbox>
                  Eu aceito os termos de uso e política de privacidade
                </Checkbox>
              </Form.Item>
            </StepContainer>
          )}

          <StepActions>
            {currentStep > 0 && (
              <Button
                style={{ height: 48, borderRadius: 8 }}
                onClick={() => prev()}
              >
                Anterior
              </Button>
            )}
            {currentStep < 3 && (
              <Button
                type="primary"
                style={{ height: 48, borderRadius: 8 }}
                onClick={() => handleNext()}
              >
                Próximo
              </Button>
            )}
            {currentStep === 3 && (
              <Button
                type="primary"
                htmlType="submit"
                style={{ height: 48, borderRadius: 8 }}
                loading={loadingAuth}
              >
                Finalizar Cadastro
              </Button>
            )}
          </StepActions>
        </Form>

        <div style={{ marginTop: 24, textAlign: "center" }}>
          <span>Já tem uma conta? </span>
          <Link
            to="/login"
            style={{ color: "var(--color-primary)", fontWeight: "bold" }}
          >
            Faça Login
          </Link>
        </div>
      </FormContainer>
    </Container>
  );
};

export default Register;
